import { useLang } from 'hooks/lang';

import { loaders, useProjectTeamChangeResponse, projectStatusByCode } from 'entities';

import { Button, IconButton, Card, Row, Table } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { CircleXIcon, DoubleCheckIcon } from 'components/Icons';

import { useUser } from 'hooks/user';
import { useProject } from 'providers/Project';
import { usePendingActions } from 'providers/PendingActions';

import { listRemove } from 'utils/array';
import { truncateText } from 'utils/text';

import { TwoColumnWrapper, UserOwnershipBreakdown } from '../components';
import { resolveColorClass } from 'styles';
import { DEFAULT_NO_OWNER_USER } from 'utils/constants';

export function ProjectTransfers() {
  const { decreaseCount } = usePendingActions();

  const user = useUser();
  const { t } = useLang();

  const { projects, loadingProjects, projectList } = useProject();

  const activeRequests = loaders.projects.activeTeamChangeRequests.use({
    defaultValue: [],
  });

  const { acceptRequest, rejectRequest } = useProjectTeamChangeResponse({
    onFinish: (id) => {
      activeRequests.overwrite((old) => listRemove(old, id, 'project'));

      decreaseCount('UNASSIGNED_RECRUITMENTS');
    },
  });

  // Projects can change teams when not active
  const actuallyActive = activeRequests.data.filter(
    ({ project }) => projects[project]?.teamChangeRequest ?? true,
  );

  return (
    <TwoColumnWrapper>
      <Card>
        <Table
          items={actuallyActive}
          loading={activeRequests.loading || loadingProjects}
          uniqueKey="id"
          columns={[
            {
              label: t('project')!,
              search: true,
              sort: true,
              getTextValue: ({ project }) => projects[project]?.name,

              render: ({ project }) => (
                <Button
                  variant="ghost"
                  to="projectProfile"
                  params={{ projectId: project }}
                  linkTarget={project}
                  tooltipContent={projects[project]?.name ?? '#REF'}
                >
                  {truncateText(projects[project]?.name ?? '#REF', 35)}
                </Button>
              ),
            },

            {
              label: t('owner')!,
              filter: true,
              getTextValue: ({ project }) => projects[project]?.owner.name,
              className: 'text-center',
              render: (request) => (
                <Row justifyContent="center">
                  <UserAvatar
                    size={32}
                    user={projects[request.project]?.owner || DEFAULT_NO_OWNER_USER}
                    name
                  />
                </Row>
              ),
            },

            {
              label: t('requested-by') as string,

              className: 'text-center',

              render: ({ requestedBy }) => (
                <Row justifyContent="center">
                  <UserAvatar size={32} user={requestedBy} name />
                </Row>
              ),
            },

            {
              label: t('team') as string,

              className: 'text-center',

              render: ({ fromTeam }) => <p>{fromTeam.name}</p>,
            },

            {
              render: (request) => (
                <Row gap="2" justifyContent="flex-end">
                  <IconButton
                    size="small"
                    tooltipContent={t('reject')}
                    onClick={() =>
                      rejectRequest({ ...projects[request.project], id: request.project })
                    }
                  >
                    <CircleXIcon size={20} color="destructive" />
                  </IconButton>

                  <IconButton
                    size="small"
                    tooltipContent={t('accept')}
                    onClick={() =>
                      acceptRequest({ ...projects[request.project], id: request.project })
                    }
                  >
                    <DoubleCheckIcon size={20} color="success" />
                  </IconButton>
                </Row>
              ),
            },
          ]}
        />
      </Card>

      <UserOwnershipBreakdown
        title={t('active-projects-per-user')!}
        statusConfig={projectStatusByCode}
        loading={loadingProjects}
        entries={projectList.filter(({ teamResponsible }) => teamResponsible === user.team.id)}
        render={(project) => (
          <Button
            variant="ghost"
            to="projectProfile"
            params={{ projectId: project.id }}
            linkTarget={project.id}
            className={resolveColorClass(projectStatusByCode[project.status].color, 'text')}
            tooltipContent={project.name}
          >
            {truncateText(project.name, 35)}
          </Button>
        )}
      />
    </TwoColumnWrapper>
  );
}
